import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
// @mui
import {
  Box,
  Stack,
  Button,
  Drawer,
  Divider,
  IconButton,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';

import { useFetcher } from 'src/api/fetcher';
import { JobService } from 'src/api';
import EmploymentStatusesList from './EmploymentStatusesList';

// ----------------------------------------------------------------------

EmploymentStatusesSidebar.propTypes = {
  openSidebar: PropTypes.bool,
  onOpenSidebar: PropTypes.func,
  onCloseSidebar: PropTypes.func,
};

export default function EmploymentStatusesSidebar({
  openSidebar,
  onOpenSidebar,
  onCloseSidebar,
}) {
  //----------------------------------------------------------------
  const [employmentStatuses, setEmploymentStatuses] = useState([]);
  const {
    data: employmentStatusesData,
  } = useFetcher(`/job/getAllJobEmploymentStatuses`);

  useMemo(() => {
    return setEmploymentStatuses(employmentStatusesData?.data || []);
  }, [employmentStatusesData?.data]);

  //----------------------------------------------------------------
  const [editing, setEditing] = useState(null);
  //----------------------------------------------------------------
  const [name, setName] = useState('');
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError();
    if (!name) {
      setError('Employment status name is required');
      return;
    }
    setError();
    if (editing) {
      handleEdit(editing, { status : name });
      return;
    }
    try {
      setLoading(true);
      const res = await JobService.addJobEmploymentStatus({ status : name });
      setName('');
      setEmploymentStatuses(prev => [...prev, res.data.data]);
    } catch (error) {
      setError('Failed to add new employment status, try again later');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    try {
      setError(null);
      const res = await JobService.deleteJobEmploymentStatus(id);
      if (res.data.successMessage) {
        setEmploymentStatuses(prev => prev.filter(c => c._id !== id));
      }
    } catch (error) {
      setError('Delete failed, try again later');
    }
  };

  const handleEdit = async (id, newEmploymentStatusName) => {
    try {
      setError(null);
      setLoading(true);
      const res = await JobService.updateJobEmploymentStatus(id, newEmploymentStatusName);
      if (res.data.successMessage) {
        setEmploymentStatuses(prev =>
          prev.map(employmentStatus =>
            employmentStatus._id === res.data.data._id
              ? res.data.data
              : employmentStatus,
          ),
        );
      }
      setEditing(null);
    } catch (error) {
      setError('Edit employment status failed, try again later');
    } finally {
      setLoading(false);
    }
  };

  useMemo(() => {
    if (editing) {
      setName(
        employmentStatuses.filter(employmentStatus => employmentStatus._id === editing)[0]
          .status,
      );
    } else {
      setName('');
    }
  }, [editing]);

  return (
    <>
      <Button
        onClick={onOpenSidebar}
        variant="outlined"
        color="secondary"
        size="small"
      >
        Employment Status
      </Button>

      <Drawer
        anchor="right"
        open={openSidebar}
        onClose={onCloseSidebar}
        PaperProps={{
          sx: { width: 320, border: 'none', overflow: 'hidden' },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1, py: 2 }}
        >
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            Employment Status
          </Typography>
          <IconButton onClick={onCloseSidebar}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>
        {error && (
          <Alert variant="standard" severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Divider />

        <Scrollbar>
          <Stack spacing={3} sx={{ px: 3 }}>
            <EmploymentStatusesList
              employmentStatuses={employmentStatuses}
              disabled={editing}
              onDelete={employmentStatusId => {
                handleDelete(employmentStatusId);
              }}
              onEdit={employmentStatusId => {
                if (editing) return;
                setEditing(employmentStatusId);
              }}
            />
            <form onSubmit={handleSubmit}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {!editing ? 'Add new' : 'Edit'} employment status
              </Typography>
              <TextField
                label="Status"
                color="secondary"
                value={name}
                onChange={e => setName(e.target.value)}
                fullWidth
                required
              />
              <Box sx={{ py: 2 }}>
                <Stack direction="row" spacing={2}>
                  {editing && !loading && (
                    <Button
                      color="error"
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={() => {
                        setError('');
                        setEditing(null);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  {!loading ? (
                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      color="inherit"
                      variant="outlined"
                    >
                      {editing ? 'Update' : 'Submit'}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      size="large"
                      color="inherit"
                      variant="outlined"
                    >
                      <CircularProgress color="inherit" size={20} />
                      &nbsp; {editing ? 'Updating...' : 'Creating'}
                    </Button>
                  )}
                </Stack>
              </Box>
            </form>
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}